import React from 'react';
import api from '../services/api';

const PermissionContext = React.createContext(null);

function readSessionData() {
  return {
    codusuario: sessionStorage.getItem('codUser'),
    nomeUsuario: (sessionStorage.getItem('usuario') || '').toUpperCase(),
    isTechnicalFlag: sessionStorage.getItem('isTechnicalUser')
  };
}

export function PermissionProvider({ children }) {
  const [sessionData, setSessionData] = React.useState(readSessionData());
  const [permissionsByModule, setPermissionsByModule] = React.useState({});
  const [loadingByModule, setLoadingByModule] = React.useState({});

  const { codusuario, nomeUsuario, isTechnicalFlag } = sessionData;

  const isTechnicalUser =
    isTechnicalFlag === 'S' || nomeUsuario === 'ADOISX';

  const refreshSession = React.useCallback(() => {
    setSessionData(readSessionData());
  }, []);

  const loadPermissions = React.useCallback(
    async (codUsuarioParam, codModuloParam, forceReload = false) => {
      if (!codUsuarioParam || !codModuloParam) {
        return [];
      }

      const moduleKey = String(codModuloParam);

      if (!forceReload && Object.prototype.hasOwnProperty.call(permissionsByModule, moduleKey)) {
        return permissionsByModule[moduleKey] || [];
      }

      if (isTechnicalUser) {
        const allPerms = ['__ALL__'];

        setPermissionsByModule((prev) => ({
          ...prev,
          [moduleKey]: allPerms
        }));

        setLoadingByModule((prev) => ({
          ...prev,
          [moduleKey]: false
        }));

        return allPerms;
      }

      setLoadingByModule((prev) => ({
        ...prev,
        [moduleKey]: true
      }));

      try {
        const response = await api.get(
          `/usuario/permissoes/${codUsuarioParam}?codmodulo=${moduleKey}`
        );

        console.log('response.data:', response.data);
        console.log('response.data.permissions:', response.data?.permissions);
        console.log('response.data.data:', response.data?.data);
        console.log('response.data.data[0]:', response.data?.data?.[0]);
        console.log(
          'response.data.data[0]?.permissions:',
          response.data?.data?.[0]?.permissions
        );

        const rawList = Array.isArray(response?.data?.permissions)
          ? response.data.permissions
          : Array.isArray(response?.data?.data?.[0]?.permissions)
            ? response.data.data[0].permissions
            : [];

        const list = rawList
          .map((p) => Number(p))
          .filter((p) => Number.isFinite(p));

        setPermissionsByModule((prev) => ({
          ...prev,
          [moduleKey]: list
        }));

        return list;
      } catch (err) {
        console.error('Erro ao buscar permissões:', err);

        setPermissionsByModule((prev) => ({
          ...prev,
          [moduleKey]: []
        }));

        return [];
      } finally {
        setLoadingByModule((prev) => ({
          ...prev,
          [moduleKey]: false
        }));
      }
    },
    [isTechnicalUser, permissionsByModule]
  );

  const hasPerm = React.useCallback(
    (codModuloParam, permCode) => {
      if (!permCode) return true;
      if (isTechnicalUser) return true;

      const moduleKey = String(codModuloParam);
      const modulePerms = permissionsByModule[moduleKey] || [];
      const permSet = new Set(modulePerms.map((p) => Number(p)));
      const result = permSet.has(Number(permCode));

      /*console.log('--- hasPerm context ---');
      console.log('codModuloParam:', codModuloParam);
      console.log('moduleKey:', moduleKey);
      console.log('permCode:', permCode);
      console.log('modulePerms:', modulePerms);
      console.log('permissionsByModule:', permissionsByModule);
      console.log('result:', result); */

      return result;
    },
    [isTechnicalUser, permissionsByModule]
  );

  const isLoading = React.useCallback(
    (codModuloParam) => {
      const moduleKey = String(codModuloParam);
      return !!loadingByModule[moduleKey];
    },
    [loadingByModule]
  );

  const clearModulePermissions = React.useCallback((codModuloParam) => {
    const moduleKey = String(codModuloParam);

    setPermissionsByModule((prev) => {
      const next = { ...prev };
      delete next[moduleKey];
      return next;
    });

    setLoadingByModule((prev) => {
      const next = { ...prev };
      delete next[moduleKey];
      return next;
    });
  }, []);

  const clearAllPermissions = React.useCallback(() => {
    setPermissionsByModule({});
    setLoadingByModule({});
  }, []);

  const value = React.useMemo(
    () => ({
      codusuario,
      nomeUsuario,
      isTechnicalUser,
      permissionsByModule,
      loadPermissions,
      hasPerm,
      isLoading,
      clearModulePermissions,
      clearAllPermissions,
      refreshSession
    }),
    [
      codusuario,
      nomeUsuario,
      isTechnicalUser,
      permissionsByModule,
      loadPermissions,
      hasPerm,
      isLoading,
      clearModulePermissions,
      clearAllPermissions,
      refreshSession
    ]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissionContext() {
  const context = React.useContext(PermissionContext);

  if (!context) {
    throw new Error('usePermissionContext deve ser usado dentro de PermissionProvider');
  }

  return context;
}